import {
  type AnalysisType,
  type MatrixAmount,
  type MatrixAmountUnit,
  type CompactPlan,
  GroupedTasks,
  Task,
  type TaskDelegation,
  type Tasks
} from '../types/types'
import { Settings } from '../hooks/useAvdeling'

export const getTaskTitle = (task?: Task): string => {
  return task?.analysisType?.sampleType?.matrix ?? '-'
}

export const matrixAmountUnitLabels: Record<MatrixAmountUnit, string> = {
  GRAM: 'g',
  KILOGRAM: 'kg',
  MILLILITER: 'ml',
  LITER: 'l',
  PIECE: 'stk'
}

export const formatMatrixAmount = (
  amount?: MatrixAmount | null
): string | undefined => {
  if (amount == null) return undefined
  const value = amount.value.toLocaleString('nb-NO', {
    maximumFractionDigits: 2
  })
  return `${value} ${matrixAmountUnitLabels[amount.unit]}`
}

export const getSubstanceText = (
  analysisType?: AnalysisType,
  separator = ', '
): string | undefined => {
  const substances = analysisType?.substances
  if (substances == null || substances.length === 0) return undefined
  return substances
    ?.map((analysisTypeSubstance) => analysisTypeSubstance.substance?.name)
    .sort((a, b) => a.localeCompare(b))
    .join(separator)
}

export type OwnerIdType = 'user' | 'region' | 'avdeling' | 'seksjon' | 'team'

const isOwnerIdType = (type: string): type is OwnerIdType => {
  return ['user', 'region', 'avdeling', 'seksjon', 'team'].includes(type)
}

export const flattenTasks = (tasks: Tasks): Tasks => {
  return tasks
}

export const getTaskHierarchyIds = (
  taskId: number,
  delegation?: TaskDelegation
): number[] => {
  const delegatedTaskIds =
    delegation?.entries.map((entry) => entry.taskDelegationId) ?? []
  return [...new Set([taskId, ...delegatedTaskIds])]
}

// Recursively collects tasks from a plan and its children
export const collectTasksRecursively = (
  planId: number,
  allPlans: CompactPlan[],
  visitedPlans = new Set<number>()
): GroupedTasks => {
  if (visitedPlans.has(planId)) return []
  visitedPlans.add(planId)

  const plan = allPlans.find((p) => p.id === planId)
  if (plan == null) return []

  let tasks: GroupedTasks = [...plan.tasks]
  if (plan.children != null) {
    plan.children.forEach((childId) => {
      tasks = [
        ...tasks,
        ...collectTasksRecursively(childId, allPlans, visitedPlans)
      ]
    })
  }
  return tasks
}

export class OwnerId {
  type: OwnerIdType
  owner: string

  constructor(type: OwnerIdType, owner: string) {
    this.type = type
    this.owner = owner
  }

  static fromString(ownerIdString: string): OwnerId | undefined {
    const ownerId = ownerIdString.split(':')
    const type = ownerId[0].toLowerCase()
    const owner = ownerId[1]
    if (type != null && owner != null && isOwnerIdType(type)) {
      return new OwnerId(type, owner)
    } else {
      return undefined
    }
  }

  toString(): string {
    return `${this.type}:${this.owner}`
  }
}

export const getOwnerId = (
  type: OwnerIdType,
  id?: string
): string | undefined => {
  return id ? new OwnerId(type, id).toString() : undefined
}

export const getOwnerIdFromSettings = (settings: Settings | undefined) => {
  return (
    getOwnerId('team', settings?.selectedTeamId) ??
    getOwnerId('seksjon', settings?.selectedSeksjonId) ??
    getOwnerId('avdeling', settings?.selectedAvdelingId) ??
    getOwnerId('region', settings?.selectedRegionId)
  )
}

export const calculateSamplesTaken = (tasks: Tasks): number => {
  return tasks?.reduce((acc, task) => acc + (task.totalCount ?? 0), 0)
}

export const calculateSamplesToTake = (tasks: Tasks): number => {
  return tasks?.reduce((acc, task) => acc + (task.quantity ?? 0), 0)
}

export const getTaskProgressText = (
  samplesTaken: number | undefined,
  samplesToTake: number | undefined
): string => {
  if (samplesTaken != null && samplesToTake != null && samplesToTake > 0) {
    return `${samplesTaken} av ${samplesToTake}`
  }

  return 'Mangler prøveantall'
}
